import { useEffect, useState, useCallback } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useMobileNav } from "@/lib/mobileNav";
import WorkspaceSwitcher from "@/components/WorkspaceSwitcher";
import {
  LayoutDashboard, Landmark, FileUp, Receipt, BookOpenCheck, CalendarX2,
  ScrollText, Users, CreditCard, Settings, Building2, BookOpen, X, Briefcase, Inbox,
  ShieldCheck, MessageSquare, HelpCircle, FileText, Repeat, History, ChevronDown, Library, TrendingUp,
} from "lucide-react";

const NAV_GROUPS = [
  {
    label: "Overview",
    testid: "nav-group-overview",
    items: [
      { to: "/app/dashboard", icon: LayoutDashboard, label: "Dashboard", testid: "nav-dashboard" },
    ],
  },
  {
    label: "Firm",
    testid: "nav-group-firm",
    enterprise: true,
    items: [
      { to: "/app/clients", icon: Briefcase, label: "Clients", testid: "nav-clients", enterprise: true },
      { to: "/app/queue", icon: Inbox, label: "Review queue", testid: "nav-queue", enterprise: true },
    ],
  },
  {
    label: "Banking",
    testid: "nav-group-banking",
    items: [
      { to: "/app/accounts", icon: Landmark, label: "Bank Accounts", testid: "nav-accounts" },
      { to: "/app/statements", icon: FileUp, label: "Statements", testid: "nav-statements" },
      { to: "/app/transactions", icon: Receipt, label: "Transactions", testid: "nav-transactions" },
      { to: "/app/reconciliation", icon: BookOpenCheck, label: "Reconciliation", testid: "nav-reconciliation" },
      { to: "/app/missing-months", icon: CalendarX2, label: "Missing Months", testid: "nav-missing-months" },
    ],
  },
  {
    label: "Receivables",
    testid: "nav-group-receivables",
    items: [
      { to: "/app/invoices", icon: FileText, label: "Invoices", testid: "nav-invoices", mutate: true },
      { to: "/app/recurring-invoices", icon: Repeat, label: "Recurring", testid: "nav-recurring-invoices", mutate: true },
      { to: "/app/reports", icon: Library, label: "General Ledger", testid: "nav-general-ledger",
        isActive: (loc) => loc.pathname === "/app/reports" && (new URLSearchParams(loc.search).get("tab") || "gl") === "gl" },
    ],
  },
  {
    label: "Reporting",
    testid: "nav-group-reporting",
    items: [
      { to: "/app/reports?tab=pnl", icon: ScrollText, label: "Reports", testid: "nav-reports",
        isActive: (loc) => loc.pathname === "/app/reports" && (new URLSearchParams(loc.search).get("tab") || "gl") !== "gl" },
    ],
  },
  {
    label: "Advisory",
    testid: "nav-group-advisory",
    items: [
      { to: "/app/advisory", icon: TrendingUp, label: "Cash, Tax & Debt", testid: "nav-advisory", staffOnly: true },
    ],
  },
  {
    label: "Workspace",
    testid: "nav-group-workspace",
    items: [
      { to: "/app/messages", icon: MessageSquare, label: "Messages", testid: "nav-messages" },
      { to: "/app/users", icon: Users, label: "Team", testid: "nav-users" },
    ],
  },
  {
    label: "Admin",
    testid: "nav-group-admin",
    items: [
      { to: "/app/audit-log", icon: History, label: "Audit log", testid: "nav-audit-log", ownerAdminOnly: true },
      { to: "/app/billing", icon: CreditCard, label: "Billing", testid: "nav-billing" },
      { to: "/app/settings", icon: Settings, label: "Settings", testid: "nav-settings" },
    ],
  },
];

export default function Sidebar() {
  const { user, tenant } = useAuth();
  const { open, closeNav } = useMobileNav();
  const location = useLocation();

  // Collapsible groups — persisted per-user in localStorage.
  const storageKey = `ll:sidebar:collapsed:${user?.email || "anon"}`;
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const raw = typeof window !== "undefined" ? window.localStorage.getItem(storageKey) : null;
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch { return new Set(); }
  });
  useEffect(() => {
    try { window.localStorage.setItem(storageKey, JSON.stringify([...collapsed])); } catch { /* ignore */ }
  }, [collapsed, storageKey]);
  const toggleGroup = useCallback((label) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label); else next.add(label);
      return next;
    });
  }, []);

  // Active-state helper — supports custom per-item isActive(loc) override,
  // falling back to pathname+search exact match for query-less items.
  const isItemActive = (item) => {
    if (typeof item.isActive === "function") return item.isActive(location);
    const [path] = item.to.split("?");
    return location.pathname === path;
  };

  return (
    <>
      {/* Mobile backdrop */}
      {open && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
          onClick={closeNav}
          data-testid="sidebar-backdrop"
        />
      )}
      <aside
        data-testid="app-sidebar"
        className={`sidebar-dark w-60 shrink-0 flex flex-col h-screen z-50 transition-transform duration-300 lg:transition-none
          fixed lg:sticky lg:top-0 inset-y-0 left-0 lg:translate-x-0
          ${open ? "translate-x-0 shadow-2xl" : "-translate-x-full lg:translate-x-0"}`}
        style={{ background: "#0b5d9e" }}
      >
        <div className="flex items-center justify-between px-5 py-5 border-b" style={{ borderColor: "var(--ll-border)" }}>
          <Link to="/app/dashboard" data-testid="sidebar-logo" className="flex items-center gap-2">
            <div className="w-7 h-7 grid place-items-center bg-[var(--ll-ink)] text-white">
              <span className="font-display font-bold text-sm">F</span>
            </div>
            <div className="font-display font-bold text-lg leading-none">Financials Ready</div>
          </Link>
          <button
            type="button"
            onClick={closeNav}
            aria-label="Close navigation"
            data-testid="sidebar-close"
            className="lg:hidden w-8 h-8 grid place-items-center hover:bg-white"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-3 py-3 border-b" style={{ borderColor: "var(--ll-border)" }}>
          <div className="flex items-start gap-2 px-2 py-2">
            <Building2 size={14} className="opacity-60 mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-xs uppercase tracking-widest text-[var(--ll-ink-muted)]">Workspace</div>
              <div
                className="text-sm font-medium leading-tight break-words line-clamp-2"
                title={tenant?.name || "—"}
                data-testid="sidebar-tenant-name"
              >
                {tenant?.name || "—"}
              </div>
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-widest mt-1 px-2 py-1 inline-block" style={{ background: "var(--ll-mint)" }}>
            Plan · {tenant?.plan || "trial"}
          </div>
          <WorkspaceSwitcher />
        </div>

        <nav className="flex-1 overflow-y-auto px-2 py-3">
          {NAV_GROUPS.map((group) => {
            if (group.enterprise && !["pro", "business"].includes(tenant?.plan)) return null;
            const items = group.items.filter((n) => {
              if (n.enterprise && !["pro", "business"].includes(tenant?.plan)) return false;
              if (n.mutate && !["owner", "admin"].includes(user?.role)) return false;
              if (n.ownerAdminOnly && !["owner", "admin"].includes(user?.role)) return false;
              if (n.staffOnly && !["owner", "admin", "accountant"].includes(user?.role)) return false;
              return true;
            });
            if (items.length === 0) return null;
            const isCollapsed = collapsed.has(group.label);
            return (
              <div key={group.label} data-testid={group.testid} className="mb-3 last:mb-0">
                <button
                  type="button"
                  onClick={() => toggleGroup(group.label)}
                  aria-expanded={!isCollapsed}
                  data-testid={`${group.testid}-toggle`}
                  className="w-full mb-1 px-3 py-1 flex items-center justify-between text-[9px] uppercase tracking-[0.25em] text-[var(--ll-ink-muted)] hover:text-[var(--ll-ink)] transition-colors"
                >
                  <span>{group.label}</span>
                  <ChevronDown
                    size={12}
                    className={`transition-transform duration-150 ${isCollapsed ? "-rotate-90" : "rotate-0"}`}
                  />
                </button>
                {!isCollapsed && (
                  <div className="space-y-0.5">
                    {items.map((n) => {
                      const active = isItemActive(n);
                      return (
                        <NavLink
                          key={n.to}
                          to={n.to}
                          end
                          data-testid={n.testid}
                          className={() => `nav-link ${active ? "active" : ""}`}
                        >
                          <n.icon size={15} />
                          <span>{n.label}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          {user?.is_platform_admin && (() => {
            const isCollapsed = collapsed.has("Platform");
            return (
              <div data-testid="nav-group-platform" className="mb-3 last:mb-0">
                <button
                  type="button"
                  onClick={() => toggleGroup("Platform")}
                  aria-expanded={!isCollapsed}
                  data-testid="nav-group-platform-toggle"
                  className="w-full mb-1 px-3 py-1 flex items-center justify-between text-[9px] uppercase tracking-[0.25em] text-[var(--ll-ink-muted)] hover:text-[var(--ll-ink)] transition-colors"
                >
                  <span>Platform</span>
                  <ChevronDown
                    size={12}
                    className={`transition-transform duration-150 ${isCollapsed ? "-rotate-90" : "rotate-0"}`}
                  />
                </button>
                {!isCollapsed && (
                  <div className="space-y-0.5">
                    <NavLink
                      to="/app/admin"
                      data-testid="nav-platform-admin"
                      className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                    >
                      <ShieldCheck size={15} />
                      <span>Platform admin</span>
                    </NavLink>
                  </div>
                )}
              </div>
            );
          })()}
        </nav>

        <div className="px-3 py-3 border-t" style={{ borderColor: "var(--ll-border)" }}>
          <div className="px-2 mb-2">
            <div className="text-xs text-[var(--ll-ink-muted)]">Signed in as</div>
            <div className="text-sm font-medium truncate" data-testid="sidebar-user-email">{user?.email}</div>
            <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-soft)]">{user?.role}</div>
          </div>
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent("ll:tour:show"))}
            data-testid="sidebar-launch-tour"
            className="w-full flex items-center justify-center gap-2 text-sm py-2 border hover:bg-[var(--ll-mint)] transition-colors mb-2"
            style={{ borderColor: "var(--ll-border)" }}
          >
            <HelpCircle size={14} /> Take the tour
          </button>
          <a
            href="/docs/user-manual"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="sidebar-help-link"
            className="w-full flex items-center justify-center gap-2 text-sm py-2 border hover:bg-[var(--ll-mint)] transition-colors"
            style={{ borderColor: "var(--ll-border)" }}
          >
            <BookOpen size={14} /> Help &amp; manuals
          </a>
        </div>
      </aside>
    </>
  );
}
